import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

export interface clientPaperlessSettings {
  offerEmail: boolean;
  offerText: boolean;
}

export interface paperlessBillingResponse {
  isEnabled: boolean;
  enrollmentState: string;
  emailSettings: clientPaperlessSettings;
  communicationPreference: string;
  currentEmail: string;
  emailIndicator: string;
  currentMobile: string;
  mobileIndicator: string;
  streetAddress1: string;
  streetAddress2: string;
  zip: string;
  city: string;
  state: string;
}

export interface paperlessBillingEnrollmentResponse {
  success: boolean;
  error: string;
}

@Injectable({
  providedIn: 'root',
})
export class PaperlessBillingService {
  private http = inject(HttpClient);

  endPointBase: string = '/PaperlessBilling/PaperlessBillingInfo';
  endpointUnenrollBase: string = '/PaperlessBilling/Unenroll';
  endPointEnrollBase: string = '/PaperlessBilling/Enroll';
  endPointEmailEnrollBase: string = '/PaperlessBilling/EnrollWithEmail';
  endPointPhoneEnrollBase: string = '/PaperlessBilling/EnrollWithMobile';
  endPointFoundationEnrollBase: string =
    '/PaperlessBilling/EnrollWithFoundation';
  endPointCityStateBase: string = '/PaperlessBilling/GetCityStateInfo';

  response: paperlessBillingResponse = {
    isEnabled: false,
    enrollmentState: '',
    emailSettings: { offerEmail: false, offerText: false },
    communicationPreference: '',
    currentEmail: '',
    emailIndicator: '',
    currentMobile: '',
    mobileIndicator: '',
    streetAddress1: '',
    streetAddress2: '',
    zip: '',
    city: '',
    state: '',
  };

  enrollmentResponse: paperlessBillingEnrollmentResponse = {
    success: false,
    error: '',
  };

  complete: boolean = false;
  completeSignal = signal<boolean>(false);

  private modalStateSource = new BehaviorSubject<boolean>(false);
  modalState$ = this.modalStateSource.asObservable();

  openModal() {
    this.fetchPaperlessBillingInfo();
    this.modalStateSource.next(true);
  }

  closeModal() {
    this.modalStateSource.next(false);
  }

  responseSubject = new BehaviorSubject<paperlessBillingResponse | null>(null);
  response$ = this.responseSubject.asObservable();

  responseSignal = signal<paperlessBillingResponse | null>(null);

  enrollmentResponseSubject =
    new BehaviorSubject<paperlessBillingEnrollmentResponse | null>(null);
  enrollmentResponse$ = this.enrollmentResponseSubject.asObservable();

  fetchPaperlessBillingInfo() {
    this.completeSignal.set(false);
    this.http
      .post<paperlessBillingResponse>(
        environment.apiUrl + this.endPointBase,
        {},
        { observe: 'response' }
      )
      .subscribe({
        next: (res: HttpResponse<paperlessBillingResponse>) => {
          if (res.status === 200 && res.body) {
            this.response = res.body;
            this.responseSignal.set(res.body);
            this.responseSubject.next(res.body);
            this.complete = true;
          }
          this.completeSignal.set(true);
        },
      });
  }

  enrollWithEmail(
    formData: unknown
  ): Observable<HttpResponse<paperlessBillingEnrollmentResponse>> {
    return this.http.post<paperlessBillingEnrollmentResponse>(
      environment.apiUrl + this.endPointEmailEnrollBase,
      formData,
      { observe: 'response' }
    );
  }

  enrollWithMobile(
    formData: unknown
  ): Observable<HttpResponse<paperlessBillingEnrollmentResponse>> {
    return this.http.post<paperlessBillingEnrollmentResponse>(
      environment.apiUrl + this.endPointPhoneEnrollBase,
      formData,
      { observe: 'response' }
    );
  }

  optOut(
    formData: unknown
  ): Observable<HttpResponse<paperlessBillingEnrollmentResponse>> {
    return this.http.post<paperlessBillingEnrollmentResponse>(
      environment.apiUrl + this.endpointUnenrollBase,
      formData,
      { observe: 'response' }
    );
  }

  getPaperlessBillingInfo() {
    return this.response; // Return data stored in the service
  }

  getEnrollmentInfo() {
    return this.enrollmentResponse;
  }
}
